import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { useFleetWrite } from './use-fleet-write.js';
import type { TerminalOption } from './use-fleet-read-model.js';

interface FleetAddVehicleProps {
  terminalOptions: TerminalOption[];
  onSuccess: () => void;
}

const CONTROL: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(7,11,18,0.72)',
  color: '#fff', fontSize: 14, boxSizing: 'border-box',
};

const LABEL: React.CSSProperties = {
  display: 'block', color: 'rgba(255,255,255,0.55)', fontSize: 12,
  textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
};

export default function FleetAddVehicle({ terminalOptions, onSuccess }: FleetAddVehicleProps) {
  const [showForm, setShowForm] = useState(false);
  const [truckNumber, setTruckNumber] = useState('');
  const [leaseType, setLeaseType] = useState('leased');
  const [terminalId, setTerminalId] = useState('');
  const [vin, setVin] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [mileage, setMileage] = useState('');

  const { createVehicle, submitting, error, clearError } = useFleetWrite(() => {
    setShowForm(false);
    setTruckNumber(''); setVin(''); setLicensePlate(''); setMileage('');
    onSuccess();
  });

  if (!showForm) {
    return (
      <button type="button" onClick={() => { setShowForm(true); clearError(); }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 8, background: C.gold, color: '#111', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 12 }}>
        Add Vehicle
      </button>
    );
  }

  const realTerminals = terminalOptions.filter((t) => t.id !== 'all');
  const canSubmit = truckNumber.trim() && terminalId && !submitting;

  function handleSubmit() {
    if (!canSubmit) return;
    createVehicle({
      truck_number: truckNumber.trim(),
      lease_type: leaseType,
      current_terminal_id: terminalId,
      vin: vin.trim() || undefined,
      license_plate: licensePlate.trim() || undefined,
      last_known_mileage: mileage ? parseInt(mileage, 10) : undefined,
    });
  }

  return (
    <div style={{ ...LAYOUT.subPanel, marginBottom: 12 }}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>Add new vehicle</div>
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', marginBottom: 12 }}>
        <div><label style={LABEL}>Truck # *</label><input type="text" value={truckNumber} onChange={(e) => setTruckNumber(e.target.value)} style={CONTROL} placeholder="e.g. 1234567" /></div>
        <div><label style={LABEL}>Lease type *</label>
          <select value={leaseType} onChange={(e) => setLeaseType(e.target.value)} style={CONTROL}>
            <option value="leased">Leased</option><option value="rental">Rental</option><option value="transit">Transit</option><option value="borrowed">Borrowed</option>
          </select>
        </div>
        <div><label style={LABEL}>Terminal *</label>
          <select value={terminalId} onChange={(e) => setTerminalId(e.target.value)} style={CONTROL}>
            <option value="">Select...</option>
            {realTerminals.map((t) => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
          </select>
        </div>
        <div><label style={LABEL}>VIN</label><input type="text" value={vin} onChange={(e) => setVin(e.target.value)} style={CONTROL} /></div>
        <div><label style={LABEL}>License plate</label><input type="text" value={licensePlate} onChange={(e) => setLicensePlate(e.target.value)} style={CONTROL} /></div>
        <div><label style={LABEL}>Mileage</label><input type="number" value={mileage} onChange={(e) => setMileage(e.target.value)} style={CONTROL} /></div>
      </div>
      {error && <div style={{ color: C.red, fontSize: 13, marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: 'rgba(255,107,107,0.1)' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={handleSubmit} disabled={!canSubmit}
          style={{ padding: '10px 20px', borderRadius: 8, background: canSubmit ? C.gold : 'rgba(255,255,255,0.06)', color: canSubmit ? '#111' : 'rgba(255,255,255,0.2)', border: 'none', fontWeight: 700, fontSize: 13, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
          {submitting ? 'Adding...' : 'Add Vehicle'}
        </button>
        <button type="button" onClick={() => { setShowForm(false); clearError(); }}
          style={{ padding: '10px 16px', borderRadius: 8, background: 'none', color: C.textMuted, border: `1px solid ${C.borderSoft}`, fontSize: 13, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
